import { useMemo, useState } from 'react';
import { chartModules } from '../domain/catalog';
import type { ChartModuleId } from '../domain/types';
import { knowledgeEntries, searchKnowledge, sourceUrlFor, type KnowledgeKind } from '../knowledge/catalog';
import { Icon } from './Icon';

const kinds: Array<KnowledgeKind | '全部'> = ['全部', '基础原典', '术数专书', '医史文献', '现代开源工具'];

export function KnowledgeLibrary() {
  const [query, setQuery] = useState('');
  const [system, setSystem] = useState<ChartModuleId | ''>('');
  const [kind, setKind] = useState<KnowledgeKind | '全部'>('全部');
  const entries = useMemo(
    () => searchKnowledge(query, system || undefined).filter((entry) => kind === '全部' || entry.kind === kind),
    [query, system, kind]
  );

  return (
    <div className="library-page page-shell">
      <header className="page-hero">
        <span className="eyebrow">可追溯知识库</span>
        <h1 tabIndex={-1}>知识谱系</h1>
        <p>查古籍、术数专书、后世注解和开源算法。每条资料都标明来源，但不把某个版本当成唯一结论。</p>
        <div className="page-metrics">
          <span><strong>{knowledgeEntries.length}</strong> 个精选条目</span>
          <span><strong>12</strong> 门术数关联</span>
          <span><strong>4</strong> 类来源层级</span>
        </div>
      </header>

      <section className="library-toolbar" aria-label="知识检索条件">
        <label className="search-field">
          <span className="visually-hidden">搜索古籍、概念或作者</span>
          <Icon name="book" size={19} />
          <input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索古籍、概念或作者" aria-label="搜索古籍、概念或作者" />
        </label>
        <label className="filter-field">
          <span>术数</span>
          <select value={system} onChange={(event) => setSystem(event.target.value as ChartModuleId | '')}>
            <option value="">全部术数</option>
            {chartModules.map((module) => <option key={module.id} value={module.id}>{module.shortName}</option>)}
          </select>
        </label>
        <div className="kind-filters" role="group" aria-label="来源类别">
          {kinds.map((item) => <button key={item} type="button" aria-pressed={kind === item} className={kind === item ? 'filter-chip is-active' : 'filter-chip'} onClick={() => setKind(item)}>{item}</button>)}
        </div>
      </section>

      <div className="library-result-line" aria-live="polite"><span>检索结果</span><strong>{entries.length}</strong></div>
      {entries.length ? (
        <div className="knowledge-grid">
          {entries.map((entry) => (
            <article className="knowledge-card" key={entry.id}>
              <div className="knowledge-meta"><span>{entry.kind}</span><time>{entry.era}</time></div>
              <h2>{entry.title}</h2>
              <p className="attribution">{entry.attribution}</p>
              <p>{entry.summary}</p>
              <ul className="concept-list" aria-label="关键概念">
                {entry.keyConcepts.map((concept) => <li key={concept}>{concept}</li>)}
              </ul>
              <div className="linked-systems">{entry.systems.length ? <>{entry.systems.slice(0, 5).map((id) => <span key={id}>{chartModules.find((item) => item.id === id)?.shortName ?? id}</span>)}{entry.systems.length > 5 && <span>+{entry.systems.length - 5}</span>}</> : <span className="linked-topic">图片研究</span>}</div>
              {entry.provenanceNote && <p className="provenance-note"><Icon name="shield" size={16} />{entry.provenanceNote}</p>}
              <a href={sourceUrlFor(entry)} target="_blank" rel="noreferrer" aria-label={`打开${entry.title}的${entry.kind === '现代开源工具' ? '开源' : '简体中文'}来源`}>
                {entry.kind === '现代开源工具' ? '查看开源来源' : '查看简体中文来源'} <Icon name="arrow" size={16} />
              </a>
            </article>
          ))}
        </div>
      ) : <div className="no-results"><h2>没有匹配条目</h2><p>尝试书名、作者、术数名，或清除筛选条件。</p></div>}
    </div>
  );
}
